import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { notificationAPI, searchAPI, createMessageWS } from '@/services/api';
import {
	Menu,
	X,
	Heart,
	MessageSquare,
	Bell,
	ChevronDown,
	LogOut,
	Shield,
	LayoutDashboard,
	Globe,
	Search,
	CreditCard,
	CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const LANGUAGES = [
  { code: 'en', label: 'English', flag: 'EN' },
  { code: 'pt', label: 'Portugues', flag: 'PT' },
  { code: 'es', label: 'Espanol', flag: 'ES' },
  { code: 'fr', label: 'Francais', flag: 'FR' },
  { code: 'de', label: 'Deutsch', flag: 'DE' },
  { code: 'pl', label: 'Polski', flag: 'PL' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
	const { user, token, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
	const [notifications, setNotifications] = useState([]);
	const [notifOpen, setNotifOpen] = useState(false);
	const notifRef = useRef(null);
	const wsRef = useRef(null);
	const reconnectTimer = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef(null);
  const searchTimeout = useRef(null);
	const [servicesOpen, setServicesOpen] = useState(false);
	const [sellMenuOpen, setSellMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

	// Global WebSocket for real-time notifications
	const connectGlobalWS = useCallback(() => {
		if (!token) return;
		try {
			const ws = createMessageWS(token);
			ws.onopen = () => {};
			ws.onmessage = (event) => {
				try {
					const data = JSON.parse(event.data);
					if (data.type === 'notification') {
						setUnreadCount(prev => prev + 1);
						toast(data.notification?.title || t('notifications.new'), {
							description: data.notification?.content,
							duration: 4000,
						});
					}
				} catch {}
			};
			ws.onclose = () => {
				reconnectTimer.current = setTimeout(connectGlobalWS, 5000);
			};
			ws.onerror = () => ws.close();
			wsRef.current = ws;
		} catch {}
	}, [token, t]);

  useEffect(() => {
    if (user) {
      notificationAPI.unreadCount().then(r => setUnreadCount(r.data.count)).catch(() => {});
			connectGlobalWS();
			// Fallback polling every 60s
      const interval = setInterval(() => {
        notificationAPI.unreadCount().then(r => setUnreadCount(r.data.count)).catch(() => {});
			}, 60000);
			return () => {
				clearInterval(interval);
				clearTimeout(reconnectTimer.current);
				if (wsRef.current) {
					wsRef.current.onclose = null;
					wsRef.current.close();
				}
			};
		}
	}, [user, connectGlobalWS]);

	// Load notifications when dropdown opens and mark unread ones as read
	useEffect(() => {
		if (notifOpen && user) {
			notificationAPI.list().then(r => {
				const notifs = r.data || [];
				setNotifications(notifs);
				const unreadIds = notifs.filter(n => !n.read).map(n => n.id);
				if (unreadIds.length > 0) {
					notificationAPI.markRead(unreadIds).then(() => {
						setUnreadCount(0);
					}).catch(() => {});
				}
			}).catch(() => {});
		}
	}, [notifOpen, user]);

	// Close notification dropdown on outside click
	useEffect(() => {
		const handler = (e) => {
			if (notifRef.current && !notifRef.current.contains(e.target)) {
				setNotifOpen(false);
			}
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, []);

  // Search autocomplete
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearchLoading(true);
      searchAPI.unified({ q: searchQuery, limit: 5 })
        .then(r => setSearchResults(r.data.items || []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearchLoading(false));
    }, 300);
    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery]);

  // Close search dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

	const onLinkClick = () => {
		setMobileOpen(false);
		window.scrollTo(0, 0);
	}

  const isHome = location.pathname === '/';
  const isTransparent = isHome && !scrolled;

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isTransparent
          ? 'bg-transparent'
          : 'bg-white/95 backdrop-blur-md border-b border-[#E6E2D6] shadow-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <div className={`text-2xl md:text-3xl font-bold font-['Playfair_Display'] tracking-tight ${isTransparent ? 
							'text-white' : 'text-[#1A3C34]'}`}>
              Equi-<span className="text-[#8B4513]">Market</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/horses"
              data-testid="nav-horses"
              className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                isTransparent ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-[#1A3C34] hover:bg-[#1A3C34]/5'
              }`}
            >
              {t('nav.horses')}
            </Link>
            <Link
              to="/products"
              data-testid="nav-products"
              className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                isTransparent ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-[#1A3C34] hover:bg-[#1A3C34]/5'
              }`}
            >
              {t('nav.products')}
            </Link>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<button
									data-testid="nav-services-btn"
									className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors flex items-center gap-1 ${
										isTransparent ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-[#1A3C34] hover:bg-[#1A3C34]/5'
									}`}
								>
									{t('nav.services')} <ChevronDown className="w-3 h-3" />
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="start" className="bg-white border-[#E6E2D6]">
								<DropdownMenuItem onClick={() => navigate('/transports')} data-testid="nav-transports">
									{t('nav.transports')}
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => navigate('/schools')} data-testid="nav-schools">
									{t('nav.schools')}
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => navigate('/stables')} data-testid="nav-stables">
									{t('nav.stables')}
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => navigate('/trainers')} data-testid="nav-trainers">
									{t('nav.trainers')}
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => navigate('/events')} data-testid="nav-events">
									{t('nav.events')}
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => navigate('/vets')} data-testid="nav-vets">
									{t('nav.vets')}
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
						<Link
							to="/map-search"
							data-testid="nav-map-search"
							className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
								isTransparent ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-[#1A3C34] hover:bg-[#1A3C34]/5'
							}`}
						>
							{t('nav.map_search')}
						</Link>
						<Link
							to="/sellers"
							data-testid="nav-sellers"
							className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
								isTransparent ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-[#1A3C34] hover:bg-[#1A3C34]/5'
							}`}
						>
							{t('nav.sellers')}
						</Link>
						<Link
							to="/faq"
							data-testid="nav-faq"
							className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
								isTransparent ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-[#1A3C34] hover:bg-[#1A3C34]/5'
							}`}
						>
							{t('faq.title')}
						</Link>

            {/* Global Search */}
            <div className="relative" ref={searchRef}>
              <button
                onClick={() => setSearchOpen(!searchOpen)}
                data-testid="nav-search-toggle"
                className={`p-2 rounded-sm transition-colors ${
                  isTransparent ? 'text-white/80 hover:text-white hover:bg-white/10' : 'text-[#1A3C34]/60 hover:text-[#1A3C34] hover:bg-[#1A3C34]/5'
                }`}
              >
                <Search className="w-5 h-5" />
              </button>
              {searchOpen && (
                <div className="absolute right-0 top-full mt-2 w-96 bg-white border border-[#E6E2D6] rounded-sm shadow-lg z-50" data-testid="search-dropdown">
                  <form onSubmit={handleSearchSubmit} className="p-3 border-b border-[#E6E2D6]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666055]" />
                      <input
                        data-testid="nav-search-input"
                        autoFocus
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t('search.placeholder')}
                        className="w-full pl-10 pr-4 py-2.5 bg-[#F9F7F2] border border-[#E6E2D6] rounded-sm text-sm text-[#1A3C34] placeholder:text-[#666055]/60 focus:outline-none focus:border-[#8B4513]"
                      />
                    </div>
                  </form>
                  {searchQuery.length >= 2 && (
                    <div className="max-h-80 overflow-auto">
                      {searchLoading ? (
                        <div className="p-4 text-center text-sm text-[#666055]">Searching...</div>
                      ) : searchResults.length > 0 ? (
                        <>
                          {searchResults.map(item => (
                            <Link
                              key={item.id}
                              to={item.result_type === 'horse' ? `/horses/${item.slug || item.id}` :
																`/products/${item.slug || item.id}`}
                              onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                              className="flex items-center gap-3 px-4 py-3 hover:bg-[#F9F7F2] transition-colors"
                              data-testid={`search-result-${item.id}`}
                            >
                              <div className="w-12 h-12 rounded-sm bg-[#F2F0E9] overflow-hidden shrink-0">
                                {item.images?.[0] ? (
                                  <img src={item.images[0].startsWith('http') ? item.images[0] :
																		`${process.env.REACT_APP_BACKEND_URL}${item.images[0]}`} alt=""
																			 className="w-full h-full object-cover" />
                                ) : null}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-[#1A3C34] truncate">{item.title}</p>
                                <p className="text-xs text-[#666055]">
																	&euro;{item.price?.toLocaleString()} &middot;
																	<span className="capitalize">{item.result_type}</span>
																</p>
                              </div>
                            </Link>
                          ))}
                          <Link
                            to={`/search?q=${encodeURIComponent(searchQuery)}`}
                            onClick={() => { setSearchOpen(false); setSearchQuery(''); }}
                            className="block px-4 py-3 text-center text-sm font-medium text-[#8B4513]
                             hover:bg-[#F9F7F2] border-t border-[#E6E2D6]"
                            data-testid="view-all-results"
                          >
														{t('nav.view_all')}
                          </Link>
                        </>
                      ) : (
                        <div className="p-4 text-center text-sm text-[#666055]">{t('nav.no_results')}</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {user && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="nav-sell-btn"
                      className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors flex items-center gap-1 ${
                        isTransparent ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-[#8B4513] ' +
													'hover:bg-[#8B4513]/5'
                      }`}
                    >
                      {t('nav.sell')} <ChevronDown className="w-3 h-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white border-[#E6E2D6]">
                    <DropdownMenuItem onClick={() => navigate('/sell/horse')}
																			data-testid="sell-horse-option">
                      {t('horse.create_ad')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/sell/product')}
																			data-testid="sell-product-option">
                      {t('product.create_ad')}
                    </DropdownMenuItem>
										<DropdownMenuItem onClick={() => navigate('/sell/transport')}
																			data-testid="sell-transport-option">
											{t('transport.create_ad')}
										</DropdownMenuItem>
										<DropdownMenuItem onClick={() => navigate('/sell/school')}
																			data-testid="sell-school-option">
											{t('school.create_title')}
										</DropdownMenuItem>
										<DropdownMenuItem onClick={() => navigate('/sell/stable')}
																			data-testid="sell-stable-option">
											{t('stable.create_title')}
										</DropdownMenuItem>
										<DropdownMenuItem onClick={() => navigate('/sell/trainer')}
																			data-testid="sell-trainer-option">
											{t('trainer.create_title')}
										</DropdownMenuItem>
										<DropdownMenuItem onClick={() => navigate('/sell/event')}
																			data-testid="sell-event-option">
											{t('event.create_title')}
										</DropdownMenuItem>
										<DropdownMenuItem onClick={() => navigate('/sell/vet')}
																			data-testid="sell-vet-option">
											{t('vet.create_title')}
										</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Link to="/dashboard?tab=favorites" className="relative p-2 group" data-testid="nav-favorites">
                  <Heart className={`w-5 h-5 transition-colors ${isTransparent ? 
										'text-white/80 group-hover:text-white' : 'text-[#1A3C34]/60 group-hover:text-[#8B4513]'}`} />
                </Link>
                <Link to="/messages" className="relative p-2 group" data-testid="nav-messages">
                  <MessageSquare className={`w-5 h-5 transition-colors ${isTransparent ? 
										'text-white/80 group-hover:text-white' : 'text-[#1A3C34]/60 group-hover:text-[#8B4513]'}`} />
                </Link>
								{/* Notification Bell Dropdown */}
								<div className="relative" ref={notifRef}>
									<button
										onClick={() => setNotifOpen(!notifOpen)}
										className={`relative p-2 transition-colors ${isTransparent ? 
											'text-white/80 hover:text-white' : 'text-[#1A3C34]/60 hover:text-[#8B4513]'}`}
										data-testid="nav-notifications-btn"
									>
										<Bell className="w-5 h-5" />
										{unreadCount > 0 && (
											<span className="absolute -top-0.5 -right-0.5 bg-[#9B2C2C] text-white text-[10px]
											font-bold rounded-full w-4 h-4 flex items-center justify-center"
														data-testid="notif-badge">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
										)}
									</button>
									{notifOpen && (
										<div className="absolute right-0 top-full mt-2 w-80 bg-white border border-[#E6E2D6]
										rounded-sm shadow-lg z-50 max-h-96 overflow-auto" data-testid="notification-dropdown">
											<div className="px-4 py-3 border-b border-[#E6E2D6]">
												<h3 className="text-sm font-semibold text-[#1A3C34]">{t('notifications.title')}</h3>
											</div>
											{notifications.length === 0 ? (
												<div className="p-6 text-center text-sm text-[#666055]">{t('notifications.empty')}</div>
											) : (
												notifications.slice(0, 10).map(n => (
													<div
														key={n.id}
														className={`px-4 py-3 border-b border-[#F2F0E9] last:border-0 hover:bg-[#F9F7F2] 
														cursor-pointer transition-colors ${!n.read ? 'bg-[#F9F7F2]' : ''}`}
														onClick={() => {
															setNotifOpen(false);
															if (n.type === 'message') navigate('/messages');
															else if (n.type === 'payment') navigate('/dashboard');
														}}
														data-testid={`notif-item-${n.id}`}
													>
														<div className="flex items-start gap-3">
															<div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 
															${n.type === 'message' ? 'bg-[#1A3C34]/10' : 'bg-[#8B4513]/10'}`}>
																{n.type === 'message' ?
																	<MessageSquare
																		className="w-4 h-4 text-[#1A3C34]" /> : n.type === 'payment' ?
																		<CreditCard className="w-4 h-4 text-[#8B4513]" />
																		:
																		<CheckCircle className="w-4 h-4 text-[#1A3C34]" />
																}
															</div>
															<div className="min-w-0 flex-1">
																<p className="text-xs font-semibold text-[#1A3C34] truncate">{n.title}</p>
																<p className="text-xs text-[#666055] truncate mt-0.5">{n.content}</p>
																<p className="text-[10px] text-[#666055]/60 mt-1">
																	{new Date(n.created_at).toLocaleDateString()}
																</p>
															</div>
															{	!n.read &&
																<div className="w-2 h-2 rounded-full bg-[#8B4513] shrink-0 mt-1.5" />
															}
														</div>
													</div>
												))
											)}
										</div>
									)}
								</div>
              </>
            )}

            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="language-switcher"
                  className={`px-2 py-2 text-sm font-medium rounded-sm transition-colors flex items-center gap-1 ${
                    isTransparent ? 'text-white/80 hover:text-white' : 'text-[#1A3C34]/60 hover:text-[#1A3C34]'
                  }`}
                >
                  <Globe className="w-4 h-4" />
                  <span className="uppercase text-xs font-bold">{i18n.language?.slice(0, 2)}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border-[#E6E2D6]">
                {LANGUAGES.map(lang => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => i18n.changeLanguage(lang.code)}
                    data-testid={`lang-${lang.code}`}
                    className={i18n.language?.startsWith(lang.code) ? 'bg-[#F2F0E9]' : ''}
                  >
                    <span className="font-bold text-xs mr-2 text-[#8B4513]">{lang.flag}</span>
                    {lang.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Auth */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="user-menu-btn"
                    className={`ml-2 flex items-center gap-2 px-3 py-1.5 rounded-sm transition-colors ${
                      isTransparent ? 'text-white hover:bg-white/10' : 'text-[#1A3C34] hover:bg-[#F2F0E9]'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full bg-[#1A3C34] flex items-center justify-center
                    text-white text-sm font-semibold">
                      {user.name?.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium hidden lg:block">{user.name?.split(' ')[0]}</span>
                    <ChevronDown className="w-3 h-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 bg-white border-[#E6E2D6]">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="nav-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> {t('nav.dashboard')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/messages')}
																		data-testid="nav-messages-menu">
                    <MessageSquare className="w-4 h-4 mr-2" /> {t('nav.messages')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/verification')}
																		data-testid="nav-verification">
                    <Shield className="w-4 h-4 mr-2" /> {t('nav.verification')}
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="nav-admin">
                      <Shield className="w-4 h-4 mr-2" /> {t('nav.admin')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout" className="text-[#9B2C2C]">
                    <LogOut className="w-4 h-4 mr-2" /> {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth" data-testid="nav-login">
                <Button className="ml-2 bg-[#1A3C34] hover:bg-[#153029] text-white rounded-sm px-6 h-9
                text-sm font-medium btn-equi">
                  {t('nav.login')}
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden p-2 ${isTransparent ? 'text-white' : 'text-[#1A3C34]'}`}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-[#E6E2D6] shadow-lg animate-fade-in
        			max-h-[calc(100vh-64px)] overflow-y-auto"
						 data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            <Link to="/horses" onClick={ onLinkClick }
									className="block px-4 py-3 text-[#1A3C34] font-medium hover:bg-[#F2F0E9] rounded-sm">
							{t('nav.horses')}
						</Link>
            <Link to="/products" onClick={ onLinkClick }
									className="block px-4 py-3 text-[#1A3C34] font-medium hover:bg-[#F2F0E9] rounded-sm">
							{t('nav.products')}
						</Link>
						<button
							onClick={() => setServicesOpen(!servicesOpen)}
							className="w-full flex items-center justify-between px-4 pt-3 pb-1 text-[10px] font-bold text-[#666055]
									uppercase tracking-wider"
						>
							{t('nav.services')}
							<ChevronDown
								size={14}
								className={`transition-transform ${servicesOpen ? 'rotate-180' : ''}`}
							/>
						</button>
						{ servicesOpen &&
							<div className="px-4 pb-2">
								<Link to="/transports" onClick={ onLinkClick }
											className="block px-4 py-3 text-[#1A3C34] font-medium hover:bg-[#F2F0E9] rounded-sm">
									{t('nav.transports')}
								</Link>
								<Link to="/schools" onClick={ onLinkClick }
											className="block px-4 py-3 text-[#1A3C34] font-medium hover:bg-[#F2F0E9] rounded-sm">
									{t('nav.schools')}
								</Link>
								<Link to="/stables" onClick={ onLinkClick }
											className="block px-4 py-3 text-[#1A3C34] font-medium hover:bg-[#F2F0E9] rounded-sm">
									{t('nav.stables')}
								</Link>
								<Link to="/trainers" onClick={ onLinkClick }
											className="block px-4 py-3 text-[#1A3C34] font-medium hover:bg-[#F2F0E9] rounded-sm">
									{t('nav.trainers')}
								</Link>
								<Link to="/events" onClick={ onLinkClick }
											className="block px-4 py-3 text-[#1A3C34] font-medium hover:bg-[#F2F0E9] rounded-sm">
									{t('nav.events')}
								</Link>
								<Link to="/vets" onClick={ onLinkClick }
											className="block px-4 py-3 text-[#1A3C34] font-medium hover:bg-[#F2F0E9] rounded-sm">
									{t('nav.vets')}
								</Link>
							</div>
						}
						<Link to="/map-search" onClick={ onLinkClick }
									className="block px-4 py-3 text-[#1A3C34] font-medium hover:bg-[#F2F0E9] rounded-sm">
							{t('nav.map_search')}
						</Link>
						<Link to="/sellers" onClick={ onLinkClick }
									className="block px-4 py-3 text-[#1A3C34] font-medium hover:bg-[#F2F0E9] rounded-sm">
							{t('nav.sellers')}
						</Link>
						<Link to="/faq" onClick={ onLinkClick }
									className="block px-4 py-3 text-[#1A3C34] font-medium hover:bg-[#F2F0E9] rounded-sm">
							{t('faq.title')}
						</Link>
            {user ? (
							<>
								<button
									onClick={() => setSellMenuOpen(!sellMenuOpen)}
									className="w-full flex items-center justify-between px-4 pt-3 pb-1 text-[10px] font-bold text-[#666055]
											uppercase tracking-wider">
									{t('nav.sell')}
									<ChevronDown
										size={14}
										className={`transition-transform ${sellMenuOpen ? 'rotate-180' : ''}`}
									/>
								</button>
								{ sellMenuOpen &&
									<div className="px-4 pb-2">
										<Link to="/sell/horse" onClick={ onLinkClick }
													className="block px-4 py-3 text-[#8B4513] font-medium hover:bg-[#F2F0E9] rounded-sm">
											{t('horse.create_ad')}
										</Link>
										<Link to="/sell/product" onClick={ onLinkClick }
													className="block px-4 py-3 text-[#8B4513] font-medium hover:bg-[#F2F0E9] rounded-sm">
											{t('product.create_ad')}
										</Link>
										<Link to="/sell/transport" onClick={ onLinkClick }
													className="block px-4 py-3 text-[#8B4513] font-medium hover:bg-[#F2F0E9] rounded-sm">
											{t('transport.create_title')}
										</Link>
										<Link to="/sell/school" onClick={ onLinkClick }
													className="block px-4 py-3 text-[#8B4513] font-medium hover:bg-[#F2F0E9] rounded-sm">
											{t('school.create_title')}
										</Link>
										<Link to="/sell/stable" onClick={ onLinkClick }
													className="block px-4 py-3 text-[#8B4513] font-medium hover:bg-[#F2F0E9] rounded-sm">
											{t('stable.create_title')}
										</Link>
										<Link to="/sell/trainer" onClick={ onLinkClick }
													className="block px-4 py-3 text-[#8B4513] font-medium hover:bg-[#F2F0E9] rounded-sm">
											{t('trainer.create_title')}
										</Link>
										<Link to="/sell/event" onClick={ onLinkClick }
													className="block px-4 py-3 text-[#8B4513] font-medium hover:bg-[#F2F0E9] rounded-sm">
											{t('event.create_title')}
										</Link>
										<Link to="/sell/vet" onClick={ onLinkClick }
													className="block px-4 py-3 text-[#8B4513] font-medium hover:bg-[#F2F0E9] rounded-sm">
											{t('vet.create_title')}
										</Link>
									</div>
								}
                <Link to="/dashboard" onClick={ onLinkClick }
											className="block px-4 py-3 text-[#1A3C34] font-medium hover:bg-[#F2F0E9] rounded-sm">
									{t('nav.dashboard')}
								</Link>
                <Link to="/messages" onClick={ onLinkClick }
											className="block px-4 py-3 text-[#1A3C34] font-medium hover:bg-[#F2F0E9] rounded-sm">
									{t('nav.messages')}
								</Link>
								<Link to="/verification" onClick={ onLinkClick }
											className="block px-4 py-3 text-[#1A3C34] font-medium hover:bg-[#F2F0E9] rounded-sm">
									{t('nav.verification')}
								</Link>
                {isAdmin && <Link to="/admin" onClick={ onLinkClick }
																	className="block px-4 py-3 text-[#1A3C34] font-medium hover:bg-[#F2F0E9]
																	rounded-sm">
									{t('nav.admin')}
								</Link>}
                <button onClick={() => { handleLogout(); setMobileOpen(false); }}
												className="block w-full text-left px-4 py-3 text-[#9B2C2C] font-medium hover:bg-[#F2F0E9]
												rounded-sm">
									{t('nav.logout')}
								</button>
              </>
            ) : (
              <Link to="/auth" onClick={ onLinkClick }
										className="block px-4 py-3 bg-[#1A3C34] text-white text-center font-medium rounded-sm">
								{t('nav.login')}
							</Link>
            )}
            {/* Language selector in mobile */}
            <div className="flex gap-1 px-4 pt-2">
              {LANGUAGES.map(l => (
                <button
                  key={l.code}
                  onClick={() => { i18n.changeLanguage(l.code); setMobileOpen(false); }}
                  className={`px-2 py-1 text-xs font-bold rounded-sm ${i18n.language?.startsWith(l.code) ? 
										'bg-[#1A3C34] text-white' : 'bg-[#F2F0E9] text-[#1A3C34]'}`}
                >
                  {l.flag}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
